import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  useMediaQuery, 
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
  Alert,
  IconButton,
  Typography,
  SelectChangeEvent,
  Chip
} from '@mui/material';
import PageHeader from '../../components/layout/PageHeader';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Clear as ClearIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { getInvoices, addInvoice, editInvoice, deleteInvoice, getInvoiceById, InvoiceDto } from '../../services/expensesApi';
import { formatEuro } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import InvoiceForm from './InvoiceForm';
import InvoiceDetail from './InvoiceDetail';
import InvoiceHomeExpenseConnection from '../../components/InvoiceHomeExpenseConnection';

const InvoiceList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<InvoiceDto | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState<InvoiceDto | null>(null);

  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [totalFilter, setTotalFilter] = useState('');
  const [sortBy, setSortBy] = useState<keyof InvoiceDto>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleTotalFilterChange = (event: SelectChangeEvent<string>) => {
    setTotalFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      setInvoices(await getInvoices());
    } catch (e: any) {
      setError(e.message || 'Não foi possível carregar as faturas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  // Filtered and sorted data
  const filteredAndSortedInvoices = useMemo(() => {
    let filtered = [...invoices];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(invoice => 
        (invoice.notes && invoice.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
        invoice.date.includes(searchTerm) ||
        invoice.total.toString().includes(searchTerm)
      );
    }

    // Apply total filter
    if (totalFilter === 'low') {
      filtered = filtered.filter(invoice => invoice.total < 100);
    } else if (totalFilter === 'medium') {
      filtered = filtered.filter(invoice => invoice.total >= 100 && invoice.total < 500);
    } else if (totalFilter === 'high') {
      filtered = filtered.filter(invoice => invoice.total >= 500);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortBy as keyof InvoiceDto] || '';
      const bValue = b[sortBy as keyof InvoiceDto] || '';
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const comparison = aValue - bValue;
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      return 0;
    });

    return filtered;
  }, [invoices, searchTerm, totalFilter, sortBy, sortOrder]);

  // Paginated data
  const paginatedInvoices = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedInvoices.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedInvoices, page, rowsPerPage]);

  // Total count for pagination
  const totalInvoices = filteredAndSortedInvoices.length;

  const handleSort = (property: keyof InvoiceDto) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTotalFilter('');
    setPage(0);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };
  const handleEdit = async (publicId: string) => {
    setLoading(true);
    try {
      const inv = await getInvoiceById(publicId);
      setEditing(inv);
      setShowForm(true);
    } finally { setLoading(false); }
  };
  const handleDelete = async (publicId: string) => {
    if (!window.confirm('Eliminar esta fatura?')) return;
    setLoading(true);
    try {
      await deleteInvoice(publicId);
      fetchInvoices();
    } finally { setLoading(false); }
  };
  const handleDetails = async (publicId: string) => {
    setLoading(true);
    try {
      setDetailInvoice(await getInvoiceById(publicId));
      setShowDetail(true);
    } finally { setLoading(false); }
  };
  const handleFormSubmit = async (data: Partial<InvoiceDto>) => {
    setLoading(true);
    try {
      if (editing && editing.publicId) {
        await editInvoice({ ...editing, ...data });
      } else {
        await addInvoice(data);
      }
      setShowForm(false);
      fetchInvoices();
    } finally { setLoading(false); }
  };

  return (
    <Box>
      <PageHeader
        title="Faturas"
        subtitle="Gerir as suas faturas."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={loading}
          >
            Adicionar fatura
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtering Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
            <TextField
              fullWidth
              size="small"
              label="Pesquisar faturas..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Intervalo de total</InputLabel>
              <Select
                value={totalFilter}
                onChange={handleTotalFilterChange}
                label="Intervalo de total"
              >
                <MenuItem value="">Todas as faturas</MenuItem>
                <MenuItem value="low">Baixo (&lt; 100 €)</MenuItem>
                <MenuItem value="medium">Médio (100 € - 500 €)</MenuItem>
                <MenuItem value="high">Alto (&gt; 500 €)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            disabled={!searchTerm && !totalFilter}
          >
            Limpar
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            A mostrar {paginatedInvoices.length} de {filteredAndSortedInvoices.length} faturas
          </Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'date'}
                  direction={sortBy === 'date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('date')}
                >
                  Data
                </TableSortLabel>
              </TableCell>
              <TableCell>Notas</TableCell>
              <TableCell align="right">Produtos</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'total'}
                  direction={sortBy === 'total' ? sortOrder : 'asc'}
                  onClick={() => handleSort('total')}
                >
                  Total
                </TableSortLabel>
              </TableCell>
              <TableCell>Despesa da casa</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInvoices.map((invoice) => (
              <TableRow key={invoice.publicId}>
                <TableCell sx={{ minWidth: 100 }}>
                  {formatDate(invoice.date)}
                </TableCell>
                <TableCell sx={{ 
                  minWidth: 120,
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {invoice.notes || '-'}
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 80 }}>
                  <Chip 
                    label={invoice.products ? invoice.products.length : 0}
                    size="small"
                    color={invoice.products && invoice.products.length > 0 ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    color={invoice.total >= 500 ? 'error.main' : invoice.total >= 100 ? 'warning.main' : 'text.primary'}
                  >
                    {formatEuro(invoice.total)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <InvoiceHomeExpenseConnection
                    invoice={invoice}
                    onConnectionChanged={fetchInvoices}
                    variant="button"
                  />
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 150 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDetails(invoice.publicId)}
                    disabled={loading}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(invoice.publicId)}
                    disabled={loading}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(invoice.publicId)}
                    disabled={loading}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedInvoices.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    Nenhuma fatura encontrada. Adicione a primeira fatura para começar.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <TablePagination
          component="div"
          count={totalInvoices}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>

      <Dialog 
        open={showForm} 
        onClose={() => setShowForm(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '90vh' }
          }
        }}
      >
        <DialogTitle>{editing ? 'Editar fatura' : 'Adicionar fatura'}</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 3 } }}>
          <InvoiceForm initial={editing || {}} onSubmit={handleFormSubmit} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>

      <Dialog 
        open={showDetail} 
        onClose={() => setShowDetail(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '90vh' }
          }
        }}
      >
        <DialogTitle>Detalhes da fatura</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 3 } }}>
          {detailInvoice && <InvoiceDetail invoice={detailInvoice} />}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={() => setShowDetail(false)} size="large" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;
